<template>
    <div class="alm-wrapper">
        <Header />
        <div class="content-wrapper">
            <div class="title-bar fs18">
                <h3 class="mb0 ">{{ localizer.Localize('ModuleSecurityDetailsView_LandscapeManagement') }}</h3>
                <div
                    class="switch-tenant-btn"
                    role="button"
                    tabindex="0"
                    @click="switchTenant">
                    <img src="../../../../public/icons/landscape/bi-directional.svg">
                    <span>{{ localizer.Localize('LandscapeManagement_TenantSwitcher') }}</span>
                    <f-icon
                        icon="chevronright"
                        :size="22"
                        color="primary"
                    />
                </div>
            </div>
            <div class="content">
                <div ref="almGrid" class="main-alm-content">
                    <div class="content-title-bar">
                        <h3 class="mb0 fs18">{{ localizer.Localize('LandscapeManagement_ChangeSets') }}</h3>
                        <f-button type="primary" @click="showCalm = true">{{ localizer.Localize('LandscapeManagement_CreateChangeSet') }}</f-button>
                    </div>
                    <div class="content-toolbar">
                        <ul class="toolbar-left-items">
                            <li>
                                <f-search-bar v-model:value="findSearchValue"
                                              :activeIndex="activeIndex"
                                              :count="searchCount"
                                              :placeholder="localizer.Localize('Find')"/>
                            </li>
                        </ul>
                        <ul class="toolbar-right-items">
                            <li id="ag-paging-row-summary-panel" />
                            <!-- <li><f-icon-button icon="recent" color="dark" :disabled="true" /></li> -->
                            <li><span class="icons-divider" /></li>
                            <li><f-icon-button icon="edit" color="dark" :title="localizer.Localize('LandscapeManagement_Edit_Changeset')" :disabled="selectedAlmChangesets?.length !== 1 || selectedAlmChangesets?.[0]?.status === STATUS.DEPLOYING" @click="handleEdit"/></li>
                            <li><span class="icons-divider" /></li>
                            <li><f-icon-button icon="delete" color="dark" :title="localizer.Localize('LandscapeManagement_Delete_Changeset')" :disabled="selectedAlmChangesets?.length !== 1 || selectedAlmChangesets?.[0]?.status === STATUS.DEPLOYING" @click="handleDelete"/></li>
                        </ul>
                    </div>
                    <f-spin :spinning="loadingChangesets" />
                    <div v-if="!changesets?.length && !loadingChangesets" class="empty-state">
                        <img src="../../../../public/icons/landscape/Runtime.svg" alt="icon">
                        <h3 class="fs18">{{ localizer.Localize('LandscapeManagement_EmptyStateTitle') }}</h3>
                        <p>{{ localizer.Localize('LandscapeManagement_EmptyStateDescription') }}</p>
                        <f-button type="primary" @click="showCalm = true">
                            {{ localizer.Localize('LandscapeManagement_CreateChangeSet') }}
                        </f-button>
                    </div>
                    <ag-grid-vue
                        v-if="changesets?.length && !loadingChangesets"
                        :row-data="rowData"
                        :column-defs="colDefs"
                        class="ag-theme-alpine landscape-ag-grid"
                        suppress-cell-focus="true"
                        :row-multi-select-with-click="true"
                        row-selection="multiple"
                        :row-height="84"
                        :pagination="true"
                        :masterDetail="true"
                        :detailCellRenderer="'AlmRowDetailsRenderer'"
                        :detailCellRendererParams="(params: any) => {
                            return {
                                masterSelected: params?.data?.id === selectedAlmChangesets?.[0]?.id && selectedAlmChangesets?.length === 1
                            };
                        }"
                        :detailRowAutoHeight="true"
                        :suppress-context-menu="true"
                        :pagination-page-size="rowsCountToFitContent"
                        :pagination-page-size-selector="paginationPageSizeSelector"
                        :pagination-auto-page-size="true"
                        row-class="landscape-ag-grid-row"
                        @row-selected="onRowSelected"
                        @grid-ready="onGridReady" />
                </div>
                <AlmRightSidebarInfo v-if="selectedAlmChangesets?.length === 1" @close="onSidebarClose" @edit="handleEdit" @deploy="handleDeployment"/>
            </div>
        </div>
    </div>
    <calm v-if="showCalm" :isVisible="showCalm" :isEditView="isEditView" @close="closeCalm" :currentChangeset="actionableChangeset"/>
    <DeleteChangeset
        v-if="showDeleteChangeset"
        :isVisible="showDeleteChangeset"
        :currentChangeset="actionableChangeset"
        @close="closeDeleteModal"
    />
    <AlmDeploymentModal
        v-if="!!changesetToDeploy"
        :isVisible="!!changesetToDeploy"
        :currentChangeset="changesetToDeploy"
        @close="changesetToDeploy = null;"
        @initiateDeployment="runDeployment"
    />
</template>

<script lang="ts">
import { mapMutations, mapActions, mapGetters } from 'vuex';
import { defineComponent } from 'vue';
import Header from '@/components/Header.vue';
import { Localizer } from '@/api/Localizer';
import Calm from '@/components/pages/landscape/Calm.vue';
import {getDeepErrorMessage} from '@/utils/Utils';
import * as api from '@/api/ApiClient';
import {AgGridVue} from 'ag-grid-vue3';
import {LicenseManager} from 'ag-grid-enterprise';
import {LicenseUtil} from '@/LicenseUtil';
import 'ag-grid-enterprise';
import AlmStatusBadge from '@/components/pages/landscape/AlmStatusBadge.vue';
import AlmCountCellRenderer from '@/components/pages/landscape/AlmCountCellRenderer.vue';
import AlmChangesetNameRenderer from '@/components/pages/landscape/AlmChangesetNameRenderer.vue';
import AlmChangesetsMenuDropdown from '@/components/pages/landscape/AlmChangesetsMenuDropdown.vue';
import AlmRightSidebarInfo from '@/components/pages/landscape/AlmRightSidebarInfo.vue';
import AlmButtonRenderer from '@/components/pages/landscape/AlmButtonRenderer.vue';
import AlmRowDetailsRenderer from '@/components/pages/landscape/AlmRowDetailsRenderer.vue';
import AlmChevronRenderer from '@/components/pages/landscape/AlmChevronRenderer.vue';
import {GridApi, GridReadyEvent} from 'ag-grid-community';
import { IChangeset, STATUS } from '@/components/pages/landscape/LandscapeManagementInterfaces';
import DeleteChangeset from '@/components/pages/landscape/DeleteChangeset.vue';
import AlmDeploymentModal from '@/components/pages/landscape/AlmDeploymentModal.vue';
export default defineComponent({
    components: {
        AlmRightSidebarInfo,
        Calm,
        Header,
        AgGridVue,
        AlmRowDetailsRenderer,
        DeleteChangeset,
        AlmDeploymentModal
    },
    data() {
        return {
            localizer: new Localizer(this.$store),
            STATUS,
            showCalm: false,
            loadingChangesets: true,
            onRowSelectedTimeout: null as ReturnType<typeof setTimeout> | null,
            gridApi: {} as GridApi,
            rowsCountToFitContent: 8,
            paginationPageSizeSelector: true as boolean | Array<number>,
            findSearchValue: '',
            activeIndex: -1,
            searchCount: 0,
            isEditView: false,
            showDeleteChangeset: false,
            changesetToDeploy: null
        };
    },
    computed: {
        ...mapGetters('alm', [
            'changesets',
            'selectedAlmChangesets',
            'actionableChangeset'
        ]),
        rowData() {
            const rows = [] as any;
            this.changesets?.forEach((changeset: IChangeset) => {
                const innerRow = {} as any;
                innerRow['id'] = changeset.id;
                innerRow['name'] = changeset.name;
                innerRow['sourceId'] = changeset.sourceTenantId;
                innerRow['source'] = changeset.sourceTenantName;
                innerRow['lastDeployed'] = changeset.lastDeployed;
                innerRow['status'] = changeset.status;
                innerRow['count'] = changeset.itemsCount;
                innerRow['tenantCount'] = changeset.tenantsCount;
                innerRow['description'] = changeset.description;
                innerRow['createdAt'] = changeset.createdAt;
                innerRow['createdBy'] = changeset.createdBy;
                innerRow['updatedAt'] = changeset.updatedAt;
                innerRow['deployedBy'] = changeset.deployedBy;
                innerRow['detail'] = changeset.changesetDetails;
                rows.push(innerRow);
            });
            return rows;
        },
        colDefs() {
            return [
                {
                    headerName: '',
                    field: 'chevron',
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    cellRenderer: AlmChevronRenderer,
                    resizable: false,
                    maxWidth: 50
                },
                {
                    headerName: '',
                    field: 'checkbox',
                    headerCheckboxSelection: true,
                    headerCheckboxSelectionFilteredOnly: true,
                    checkboxSelection: true,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    resizable: false,
                    maxWidth: 40,
                    cellStyle: { paddingBottom: '36px' },                    
                },
                {
                    headerName: this.localizer.Localize('All'),
                    field: 'name',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    flex: 2,
                    resizable: false,
                    cellRenderer: AlmChangesetNameRenderer,
                    tooltipValueGetter: (p:any) => p.value,
                    getQuickFilterText: (params: any) => params.value
                },
                { 
                    headerName: '',
                    field: 'tenantCount',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    resizable: false,
                    cellRenderer: AlmCountCellRenderer,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'right' },
                    getQuickFilterText: (): string => ""
                },
                { 
                    headerName: '',
                    field: 'count',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    resizable: false,
                    cellRenderer: AlmCountCellRenderer,
                    maxWidth: 100,
                    cellStyle: { textAlign: 'right' },
                    getQuickFilterText: (): string => ""
                },
                {
                    headerName: '',
                    field: 'status',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    resizable: false,
                    cellRenderer: AlmStatusBadge,
                    cellRendererParams: () => {
                        return {
                            showBorder: true,
                        };
                    },
                    cellStyle: { textAlign: 'right' },
                    maxWidth: 130,
                    getQuickFilterText: (): string => ""
                },
                {
                    headerName: '',
                    field: 'button',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    resizable: false,
                    cellRenderer: AlmButtonRenderer,
                    cellRendererParams: {
                        onDeploy: this.handleDeployment
                    },
                    cellStyle: { textAlign: 'right' },
                    maxWidth: 80,
                    getQuickFilterText: (): string => ""
                },
                {
                    headerName: '',
                    field: 'menu',
                    sortable: false,
                    suppressHeaderMenuButton: true,
                    suppressColumnsToolPanel: true,
                    suppressMovable: true,
                    resizable: false,
                    cellRenderer: AlmChangesetsMenuDropdown,
                    cellRendererParams:  {
                        onEdit: this.handleEdit,
                        onExport: this.handleExport,
                        onDuplicate: this.handleDuplicate,
                        onDelete: this.handleDelete,
                        onCancelDeployment: this.handleCancelDeployment,
                    },
                    maxWidth: 42
                }
            ];
        }
    },
    watch: {
        findSearchValue(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.gridApi.setGridOption(
                    'quickFilterText',
                    this.findSearchValue
                );
            }
        }
    },
    async mounted() {
        const licenseUtil = new LicenseUtil();
        LicenseManager.setLicenseKey(licenseUtil.agGridLicense);
        try {
            await this.getChangesets();
        }
        catch (e) {
            const errorMessage = getDeepErrorMessage(e);
            await this.ShowToast(new api.ToastNotification({
                message: errorMessage || this.localizer.Localize('Failed_status'),
                type: api.ToastType.Error
            }));
        }
        finally {
            this.loadingChangesets = false;
        }
    },
    async beforeMount() {
        await this.SetALMNotificationHandler();
    },
    beforeUnmount() {
        this.setSelectedAlmChangesets(null);
    },
    methods: {
        ...mapMutations('alm', [
            'setAlmView'
        ]),
        ...mapActions('alm', [
            'getChangesets',
            'RunALMDeployment',
            'SetALMNotificationHandler'
        ]),
        ...mapActions([ 'ShowToast' ]),
        ...mapMutations('alm', [
            'setSelectedAlmChangesets',
            'setActionableChangeset'
        ]),
        calculateRowsCountsToFitContent() {
            const autoFitRowsCount = this.gridApi.paginationGetPageSize();
            this.rowsCountToFitContent = autoFitRowsCount;
            this.gridApi.updateGridOptions({
                paginationAutoPageSize: false,
                paginationPageSize: autoFitRowsCount
            });
            this.paginationPageSizeSelector = [ autoFitRowsCount, 20, 50, 100 ];

            const rowSummaryPanelEl = document.querySelector('.ag-paging-row-summary-panel');
            const rowSummaryPanelAlmEl = document.querySelector('#ag-paging-row-summary-panel');
            rowSummaryPanelEl && rowSummaryPanelAlmEl?.appendChild(rowSummaryPanelEl);
        },
        switchTenant() {
            this.setAlmView(false);
        },
        onGridReady(params: GridReadyEvent) {
            this.gridApi = params.api;
            this.calculateRowsCountsToFitContent();
        },
        onRowSelected() {
            // Timeout need to prevent performance issue for multiple row selections
            this.onRowSelectedTimeout && clearTimeout(this.onRowSelectedTimeout);
            this.onRowSelectedTimeout = setTimeout(() => {
                try {
                    this.onRowSelectedTimeout = null;
                    this.setSelectedAlmChangesets(this.gridApi?.getSelectedRows && this.gridApi?.getSelectedRows() || []);
                }
                catch (e) {
                    console.error(e);
                }
            }, 100);
        },
        onSidebarClose() {
            this.gridApi.deselectAll();
        },
        handleEdit() {
            this.isEditView = true;
            this.showCalm = true;
        },
        handleDelete() {
            this.showDeleteChangeset = true;
        },
        handleDuplicate(rowData: any) {
            console.log('Duplicate changeset:', rowData);
            // TODO: Implement duplicate functionality
        },
        handleExport(rowData: any) {
            console.log('Export changeset:', rowData);
            // TODO: Implement export functionality
        },
        handleCancelDeployment(rowData: any) {
            console.log('Cancel deployment:', rowData);
            // TODO: Implement cancel deployment functionality
        },
        closeCalm(){
            this.showCalm = false; 
            this.isEditView = false;
            this.setActionableChangeset(null);
        },
        closeDeleteModal() {
            this.showDeleteChangeset = false;
            this.setActionableChangeset(null);
        },
        handleDeployment(data: any) {
            this.changesetToDeploy = data;
        },
        async runDeployment() {
            if (this.changesetToDeploy) {
                await this.RunALMDeployment(this.changesetToDeploy);
                this.changesetToDeploy = null;
            }
        }
    }
});
</script>

<style scoped>
.landscape-ag-grid {
    --ag-odd-row-background-color: none;
    --ag-grid-size: 8px;
    --ag-row-height: 58px;
    --ag-header-height: 58px;
    --ag-borders: none;
    --ag-row-border-color: var(--accent-color-blue-12);
    --ag-checkbox-checked-color: var(--accent-color-blue);
    --ag-checkbox-indeterminate-color: var(--accent-color-blue);
    --ag-input-focus-box-shadow: none;
    --ag-icon-size: 20px;
    --ag-cell-horizontal-padding: 0px;
    --ag-border-radius: 2px;
    --ag-icon-font-color: var(--accent-color5);
}
.content-wrapper {
    border-top: solid 1px var(--accent-color3);
    height: calc(100% - 46px);
}

:deep(#header) {
    position: initial;
    border-bottom: solid 1px var(--accent-color3);
}

.alm-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
}

:deep(.headerbtnmain .button.icon-button.default) {
    height: 100%;
}

.content-wrapper .title-bar {
    border-bottom: solid 1px var(--accent-color3);
    height: 46px;
    display: flex;
    align-items: center;
    padding-left: 16px;
    padding-right: 16px;
    justify-content: space-between;
}

.switch-tenant-btn {
    display: flex;
    align-items: center;
    cursor: pointer;
}

.switch-tenant-btn span {
    font-size: 14px;
    margin-left: 8px;
    margin-right: 4px;
}

.fs18 {
    font-size: 18px;
}

.content-title-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    height: 56px;
    border-bottom: 1px solid var(--accent-color3);
    padding-right: 16px;
}

.content {
    display: flex;
    height: calc(100% - 46px);
}

.main-alm-content {
    flex-grow: 1;
    padding-left: 16px;
    display: flex;
    flex-direction: column;
}

.toolbar-left-items,
.toolbar-right-items {
    display: flex;
    align-items: center;
    list-style: none;
    padding: 0;
    margin: 0;
}

.icons-divider {
    display: inline-block;
    background: #E6EBF8;
    width: 1px;
    height: 24px;
    vertical-align: middle;
}

.content-toolbar {
    display: flex;
    justify-content: space-between;
    padding-top: 20px;
    padding-bottom: 20px;
    padding-right: 16px;
}

.empty-state {
    margin-top: 40px;
    align-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 338px;
}

.empty-state h3 {
    margin-top: 20px;
}

.empty-state p {
    text-align: center;
    line-height: 16.8px;
    margin-bottom: 20px;
}

.toolbar-right-items li {
    margin-left: 8px;
}

.toolbar-left-items li {
    margin-right: 8px;
}

.landscape-ag-grid {
    padding-bottom: 16px;
    width: 100%;
    height: 100%;
}

.landscape-ag-grid :deep(.ag-cell-wrapper) {
    height: 100%;
}

:deep(.ag-checkbox-input-wrapper:not(.ag-checked, .ag-indeterminate)::after) {
    border: solid 1px;
    content: '';
    width: 100%;
    height: 100%;
    border-radius: 2px;
}

:deep(.landscape-ag-grid.ag-theme-alpine .ag-header-row) {
    font-weight: 600;
    font-size: 14px;
}

:deep(.landscape-ag-grid-row) {
    border-bottom: none;
    border-top: var(--ag-row-border-style) var(--ag-row-border-color) var(--ag-row-border-width);
}

:deep(.landscape-ag-grid .ag-paging-page-size .ag-label) {
    font-size: 13px;
    font-weight: 600;
}

:deep(.landscape-ag-grid .ag-paging-panel),
:deep(.landscape-ag-grid .ag-sticky-bottom) {
    border: none;
}

:deep(.landscape-ag-grid .ag-paging-row-summary-panel) {
    opacity: 0;
}

:deep(.landscape-ag-grid .ag-picker-field-wrapper) {
    background: var(--accent-color4);
    border-bottom: 1px solid var(--accent-color5);
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
}

:deep(.landscape-ag-grid .ag-paging-description .ag-paging-number) {
    font-weight: 500;
}

:deep(.landscape-ag-grid .ag-paging-description > span:first-child) {
    display: none;
}

:deep(.landscape-ag-grid [col-id="status"]) {
    padding-left: 0;
    padding-right: 0;
}

:deep(.landscape-ag-grid [col-id="menu"]) {
    padding-left: 0;
}

:deep(.landscape-ag-grid .ag-center-cols-viewport) {
    padding-right: 16px;
}

:deep(.landscape-ag-grid .ag-paging-button.ag-disabled) {
    opacity: 0.5;
}

:deep(.ag-paging-row-summary-panel-number) {
    font-weight: 600;
}
:deep(.ag-column-first) {
    padding-left: 24px;
    padding-right: 0px;
}
</style>
